import axios from "axios";
import authHeader from "./auth-header";
import AuthService from "./auth.service";
import { config } from "../config";

class NotificationsService {
  // Handle errors centrally
  _handleError(error) {
    if (error.response?.status == 401) {
      AuthService.logout();
      window.location = "/login";
    }
    console.log("ERROR:: ", error.response?.data || error.message);
    return null;
  }

  validadeTokenExist(bodyParms) {
    return axios
      .post(`${config["apiUrl"]}notifications/tokenExist`, bodyParms,  {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        return null;
      })
      .catch(error => this._handleError(error));
  }

  // ============================================
  // PROVIDERS
  // ============================================

  /**
   * Get all notification providers
   * @returns {Promise<Array>} List of providers with their templates
   */
  getAllProviders() {
    return axios
      .get(`${config["apiUrl"]}notifications/`, { headers: authHeader() })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  // ============================================
  // TEMPLATES
  // ============================================

  /**
   * Get all templates for a specific provider
   * @param {string} provider - Provider code (e.g., 'sms', 'email')
   * @returns {Promise<Object>} Provider and templates data
   */
  getTemplates(provider) {
    return axios
      .get(`${config["apiUrl"]}notifications/${provider}/templates`, { headers: authHeader() })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  /**
   * Get a specific template
   * @param {string} provider - Provider code
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Template data
   */
  getTemplate(provider, templateId) {
    return axios
      .get(`${config["apiUrl"]}notifications/${provider}/templates/${templateId}`, {
        headers: authHeader()
      })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  /**
   * Create a new template
   * @param {string} provider - Provider code
   * @param {Object} templateData - Template data
   * @param {string} templateData.code - Unique template code
   * @param {string} templateData.name - Template name
   * @param {string} templateData.content - Template content with {{tags}}
   * @param {string} [templateData.subject] - Email subject (optional)
   * @param {string} [templateData.description] - Description (optional)
   * @param {boolean} [templateData.is_active] - Active status (default: true)
   * @returns {Promise<Object>} Created template
   */
  createTemplate(provider, templateData) {
    return axios
      .post(
        `${config["apiUrl"]}notifications/${provider}/templates`,
        templateData,
        { headers: authHeader() }
      )
      .then(response => {
        if (response.status == 201 || response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  /**
   * Update an existing template
   * @param {string} provider - Provider code
   * @param {number} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template
   */
  updateTemplate(provider, templateId, templateData) {
    return axios
      .put(
        `${config["apiUrl"]}notifications/${provider}/templates/${templateId}`,
        templateData,
        { headers: authHeader() }
      )
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  /**
   * Delete a template
   * @param {string} provider - Provider code
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Success response
   */
  deleteTemplate(provider, templateId) {
    return axios
      .delete(
        `${config["apiUrl"]}notifications/${provider}/templates/${templateId}`,
        { headers: authHeader() }
      )
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  // ============================================
  // SETTINGS
  // ============================================

  /**
   * Get provider settings (API keys, credentials, etc)
   * @param {string} provider - Provider code
   * @returns {Promise<Object>} Provider settings
   */
  getNotificationConfig(provider) {
    return axios
      .get(`${config["apiUrl"]}notifications/settings/${provider}`, {
        headers: authHeader()
      })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  /**
   * Save provider settings
   * @param {string} provider - Provider code
   * @param {Object} params - Settings data
   * @param {Array} params.settings - Array of setting objects
   * @param {string} params.settings[].key - Setting key
   * @param {string} params.settings[].value - Setting value
   * @param {boolean} [params.settings[].is_encrypted] - Whether to encrypt value
   * @returns {Promise<Object>} Success response
   */
  saveNotificationConfig(provider, params) {
    return axios
      .post(
        `${config["apiUrl"]}notifications/settings/${provider}`,
        params,
        { headers: authHeader() }
      )
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  // ============================================
  // TEST
  // ============================================

  /**
   * Send a test notification
   * @param {string} provider - Provider code
   * @param {number} templateId - Template ID
   * @param {Object} testData - Test data
   * @param {string} testData.recipient - Recipient (phone/email)
   * @param {Object} testData.data - Data to replace template tags
   * @returns {Promise<Object>} Test result with rendered content
   */
  sendTestNotification(provider, templateId, testData) {
    return axios
      .post(
        `${config["apiUrl"]}notifications/${provider}/templates/${templateId}/test`,
        testData,
        { headers: authHeader() }
      )
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  getSmsLogs(params) {
    return axios
      .get(`${config["apiUrl"]}notifications/sms/logs`, {
        headers: authHeader(),
        params: params,
      })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }

  getLogs(params) {
    return axios
      .get(`${config["apiUrl"]}notifications/logs`, {
        headers: authHeader(),
        params: params,
      })
      .then(response => {
        if (response.status == 200) {
          return response.data;
        }
      })
      .catch(error => this._handleError(error));
  }
}

export default new NotificationsService();